// Reading signing participants without anyone's full national ID.
//
// Workflow reads carry each participant's national ID masked and `is_me`,
// computed by the server for the caller: their slot by account
// (`user_uuid`), or by the national ID of one of their registered
// certificates. When a read says `is_me`, that is the answer; a read without
// it (the slot as another reader saw it) matches by account alone. The masked
// number only helps pick which of the caller's own certificates fits a slot
// (the server checks again when the signature arrives).

import { currentUserUuid } from '@/shared/auth/me.js';
import { maskNationalId, nationalIdsMayMatch } from '@/shared/utils/nationalId';
import { field } from './signingState';

/** The participant's national ID as read (masked by the server), or null. */
export const participantNationalId = (p) =>
  field(p, 'national_id_masked') || field(p, 'national_id_value') || null;

/** For display: always masked, whatever the read held. */
export const participantIdLabel = (p) => maskNationalId(participantNationalId(p));

/**
 * This slot is `me`'s (docs' uuid of a person; default: whoever is signed
 * in). The server's `is_me` wins; without it, the slot's `user_uuid`.
 */
export function participantIsMe(participant, me = currentUserUuid()) {
  const flag = field(participant, 'is_me');
  if (typeof flag === 'boolean') return flag;
  const userUuid = field(participant, 'user_uuid');
  return Boolean(userUuid) && Boolean(me) && String(userUuid) === String(me);
}

/** A certificate's PINFL — what a signing slot names (slots take 14-digit PINFLs). */
export const certNationalId = (cert) => cert?.PINFL || cert?.pinfl || null;

/** A slot named by national ID takes a certificate with that ID; one named by account alone takes any. */
export function certFitsSlot(cert, p) {
  const id = participantNationalId(p);
  return !id || nationalIdsMayMatch(certNationalId(cert), id);
}

/**
 * The selected certificate may fill this slot: it is the caller's slot and
 * the certificate fits — or the certificate isn't registered with docs yet
 * (so the server can't know it is the caller's) and its ID matches the slot's.
 * Signing registers it first; the server decides then.
 */
export function slotTakesCert(p, cert, me = currentUserUuid()) {
  if (!cert || !certFitsSlot(cert, p)) return false;
  if (participantIsMe(p, me)) return true;
  return !cert.isRegistered && Boolean(participantNationalId(p));
}
